import { DownloadSimple, Funnel, MagnifyingGlass, Warning } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import type { DiffStatus, DiffSummary, MatchResult } from '../../types'
import { DIFF_STATUS_LABELS, resultSearchText } from '../../ui/formatters'
import { DetailDrawer } from './DetailDrawer'
import { SummaryGrid } from './SummaryGrid'
import { VirtualDiffTable } from './VirtualDiffTable'

type StatusFilter = 'all' | DiffStatus
type FieldFilter = 'quantity' | 'unitPrice' | 'itemDescription'

interface ResultsViewProps {
  exporting: boolean
  onExport: () => void
  onReviewPending: () => void
  results: readonly MatchResult[]
  summary: DiffSummary
}

const statuses: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: '全部' },
  { id: 'added', label: DIFF_STATUS_LABELS.added },
  { id: 'removed', label: DIFF_STATUS_LABELS.removed },
  { id: 'changed', label: DIFF_STATUS_LABELS.changed },
  { id: 'unchanged', label: DIFF_STATUS_LABELS.unchanged },
  { id: 'pending', label: DIFF_STATUS_LABELS.pending },
]

export function ResultsView({
  exporting,
  onExport,
  onReviewPending,
  results,
  summary,
}: ResultsViewProps) {
  const [status, setStatus] = useState<StatusFilter>('all')
  const [fieldFilters, setFieldFilters] = useState<ReadonlySet<FieldFilter>>(new Set())
  const [keyword, setKeyword] = useState('')
  const [selected, setSelected] = useState<MatchResult | null>(null)

  const filtered = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase('zh-CN')
    return results.filter((result) => {
      if (status !== 'all' && result.status !== status) return false
      if (
        fieldFilters.size > 0 &&
        !Array.from(fieldFilters).every((field) => result.fieldChanges[field].changed)
      ) {
        return false
      }
      return normalizedKeyword.length === 0 || resultSearchText(result).includes(normalizedKeyword)
    })
  }, [fieldFilters, keyword, results, status])

  const toggleField = (field: FieldFilter): void => {
    setFieldFilters((current) => {
      const next = new Set(current)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  return (
    <>
      <header className="results-heading">
        <div>
          <h1>差异结果</h1>
          <p>按状态和变化字段筛选，选择任一清单项查看逐字段依据。</p>
        </div>
      </header>
      <SummaryGrid summary={summary} />

      {summary.pendingCount > 0 ? (
        <div className="pending-banner">
          <Warning aria-hidden="true" weight="fill" />
          <div>
            <strong>还有 {summary.pendingCount} 条待确认匹配</strong>
            <span>这些项目暂不计入已确认净金额影响。</span>
          </div>
          <button className="button button--secondary" type="button" onClick={onReviewPending}>
            继续复核
          </button>
        </div>
      ) : null}

      <section className="result-toolbar" aria-label="结果筛选">
        <div className="status-tabs" role="group" aria-label="按状态筛选">
          {statuses.map((item) => (
            <button
              type="button"
              data-active={status === item.id}
              data-testid={`filter-${item.id}`}
              aria-pressed={status === item.id}
              key={item.id}
              onClick={() => setStatus(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="result-toolbar__second-row">
          <div className="field-filter-group" role="group" aria-label="按变化字段筛选">
            <Funnel aria-hidden="true" />
            <button
              type="button"
              data-active={fieldFilters.has('quantity')}
              aria-pressed={fieldFilters.has('quantity')}
              onClick={() => toggleField('quantity')}
            >
              只看工程量变化
            </button>
            <button
              type="button"
              data-active={fieldFilters.has('unitPrice')}
              aria-pressed={fieldFilters.has('unitPrice')}
              onClick={() => toggleField('unitPrice')}
            >
              只看综合单价变化
            </button>
            <button
              type="button"
              data-active={fieldFilters.has('itemDescription')}
              aria-pressed={fieldFilters.has('itemDescription')}
              onClick={() => toggleField('itemDescription')}
            >
              只看项目特征变化
            </button>
          </div>
          <label className="result-search">
            <span className="sr-only">关键字搜索</span>
            <MagnifyingGlass aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索编码、名称或项目特征"
            />
          </label>
          <button
            className="button button--primary"
            type="button"
            onClick={onExport}
            disabled={exporting}
          >
            <DownloadSimple aria-hidden="true" />
            {exporting ? '正在生成' : '导出 Excel'}
          </button>
        </div>
      </section>

      <div className="result-count-line">
        当前显示 <strong>{filtered.length.toLocaleString('zh-CN')}</strong> 条，共{' '}
        {results.length.toLocaleString('zh-CN')} 条
      </div>
      <VirtualDiffTable
        results={filtered}
        selectedId={selected?.id ?? null}
        onSelect={setSelected}
      />
      {selected ? <DetailDrawer result={selected} onClose={() => setSelected(null)} /> : null}
    </>
  )
}
